// React
import { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
// Componentes externos
import { Button, Container, Modal, Tab, Tabs, Form } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import {
  faTrash,
  faRotateLeft,
  faEdit,
  faSquarePlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Componentes
import CustomTable from "../../../components/CustomTable/CustomTable";
import { Loading } from "../../../components/Loading/Loading";
// Hooks
import { useMutationData } from "../../../hooks/useMutationData";
import { useGetData } from "../../../hooks/useGetData";
import { useQueryClient } from "@tanstack/react-query";
// Interfaces
import { RolInterface } from "../../../interfaces/RolInterface";
import { BloqueInterface } from "../../../interfaces/BloqueInterface";
// Estilo
import classes from "./RolListaScreen.module.css";
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";
// Contexto del tema
import { AuthContext } from "../../../context/AuthContext";
import { useBloquesContext } from "../../../context/BloquesContext";

export const RolListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const { usuario } = useContext(AuthContext);
  const { refetchBloques } = useBloquesContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedRol, setSelectedRol] = useState<RolInterface | null>(null);
  const [nombre, setNombre] = useState("");
  const { reset } = useForm();

  const uuid = usuario?.uuid;

  const mutateRol = useMutationData({
    url: "/rol",
    reload: ["rol"],
  });
  const queryRol = useGetData<RolInterface[]>({ url: "/rol", name: ["rol"] });

  const queryPermisosDelRol = useGetData<{ id: number }[]>({
    url: selectedRol ? `/rol/${selectedRol.id}/permisos` : "/rol/permisos",
    name: ["rol-permisos", selectedRol?.id?.toString() || ""],
    condition: !!selectedRol,
  });

  const queryPermisos = useGetData<{ id: number; nombre: string }[]>({
    url: "/permiso",
    name: ["permiso"],
  });

  const mutatePermiso = useMutationData({
    url: selectedRol ? `/rol/${selectedRol.id}/permiso` : "",
    reload: ["permiso"],
  });

  const queryBloquesDelRol = useGetData<{ id: number }[]>({
    url: selectedRol ? `/rol/${selectedRol.id}/bloque` : "/rol/bloque",
    name: ["rol-bloques", selectedRol?.id?.toString() || ""],
    condition: !!selectedRol,
  });

  const queryBloques = useGetData<BloqueInterface[]>({
    url: "/bloque",
    name: ["bloque"],
  });

  const mutateBloque = useMutationData({
    url: selectedRol ? `/rol/${selectedRol.id}/bloque` : "",
    reload: ["bloque"],
  });

  const handleEditClick = (rol: RolInterface) => {
    setSelectedRol(rol);
    setNombre(rol.nombre);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRol(null);
  };

  const handleSaveChanges = () => {
    if (!selectedRol) return;

    mutateRol.mutate({
      id: selectedRol.id,
      data: { nombre },
      tipo: "patch",
    });
    setShowModal(false);
  };

  const handleTogglePermission = async (
    permisoId: number,
    isActive: boolean
  ) => {
    if (!selectedRol) return;

    try {
      if (isActive) {
        await mutatePermiso.mutateAsync({
          data: { permiso_id: permisoId },
          tipo: "post",
        });
      } else {
        await mutatePermiso.mutateAsync({
          tipo: "delete",
          id: permisoId,
        });
      }

      queryPermisosDelRol.refetch();
    } catch (error) {
      console.error("Error al modificar permisos:", error);
    }
  };

  const handleToggleBlock = async (bloqueId: number, isActive: boolean) => {
    if (!selectedRol) return;

    try {
      if (isActive) {
        await mutateBloque.mutateAsync({
          data: { bloque_id: bloqueId },
          tipo: "post",
        });
      } else {
        await mutateBloque.mutateAsync({
          tipo: "delete",
          id: bloqueId,
        });
      }

      // Refrescar los bloques del rol editado
      queryBloquesDelRol.refetch();
      
      // Si el rol editado es el mismo que el del usuario logueado, refrescar el contexto
      console.log("🔄 Comparando roles:", {
        selectedRolId: selectedRol.id,
        usuarioRolId: usuario?.rol_id,
        sonIguales: selectedRol.id === usuario?.rol_id
      });
      
      if (selectedRol.id === usuario?.rol_id) {
        console.log("🔄 Refrescando contexto de bloques del usuario logueado");
        // Invalidar las queries relacionadas con bloques del usuario
        queryClient.invalidateQueries({ queryKey: ["usuario-bloques"] });
        refetchBloques();
      }
    } catch (error) {
      console.error("Error al modificar bloques:", error);
    }
  };

  const permisosColumns = useMemo<
    ColumnDef<{ id: number; nombre: string; estatus: number }>[]
  >(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Nombre",
        accessorKey: "nombre",
      },
      {
        header: "Estatus",
        accessorFn: (row) =>
          queryPermisosDelRol.data?.some((p) => p.id === row.id)
            ? "Activo"
            : "Inactivo",
        cell: (info) => info.getValue(),
      },
      {
        header: "Acciones",
        cell: (info) => (
          <Form.Check
            type="switch"
            id={`permiso-switch-${info.row.original.id}`}
            checked={queryPermisosDelRol.data?.some(
              (p) => p.id === info.row.original.id
            )}
            onChange={(e) =>
              handleTogglePermission(info.row.original.id, e.target.checked)
            }
          />
        ),
      },
    ],
    [queryPermisosDelRol.data]
  );

  const bloquesColumns = useMemo<
    ColumnDef<{ id: number; nombre: string; estatus: number }>[]
  >(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Nombre",
        accessorKey: "nombre",
      },
      {
        header: "Estatus",
        accessorFn: (row) =>
          queryBloquesDelRol.data?.some((p) => p.id === row.id)
            ? "Activo"
            : "Inactivo",
        cell: (info) => info.getValue(),
      },
      {
        header: "Acciones",
        cell: (info) => (
          <Form.Check
            type="switch"
            id={`bloque-switch-${info.row.original.id}`}
            checked={queryBloquesDelRol.data?.some(
              (p) => p.id === info.row.original.id
            )}
            onChange={(e) =>
              handleToggleBlock(info.row.original.id, e.target.checked)
            }
          />
        ),
      },
    ],
    [queryBloquesDelRol.data]
  );

  console.log("Bloques activos:", queryBloquesDelRol.data);
  
  console.log(
    "Bloque 2 activo:",
    queryBloquesDelRol.data?.some((p) => p.id === 2)
  );
  console.log(
    "Bloque 4 activo:",
    queryBloquesDelRol.data?.some((p) => p.id === 4)
  );
  console.log(
    "Bloque 5 activo:",
    queryBloquesDelRol.data?.some((p) => p.id === 5)
  );
  console.log(
    "Bloque 6 activo:",
    queryBloquesDelRol.data?.some((p) => p.id === 6)
  );
  console.log(
    "Bloque 7 activo:",
    queryBloquesDelRol.data?.some((p) => p.id === 7)
  );

  // NO actualizar localStorage aquí - esto causaba el problema
  // El localStorage solo debe actualizarse para el usuario logueado
  // useEffect(() => {
  //   if (queryBloquesDelRol.data) {
  //     localStorage.setItem('bloquesActivos', JSON.stringify(queryBloquesDelRol.data));
  //   }
  // }, [queryBloquesDelRol.data]);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDarkMode);
  }, [isDarkMode]);

  if (
    queryRol.isLoading ||
    queryPermisos.isLoading ||
    queryPermisosDelRol.isLoading
  ) {
    return <Loading />;
  }

  if (
    queryRol.isLoading ||
    queryBloques.isLoading ||
    queryBloquesDelRol.isLoading
  ) {
    return <Loading />;
  }

  return (
    <div
      className={`p-6 min-h-screen ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"
      }`}
    >
      <div className="mb-6 flex justify-between items-center">
        <h2>Roles</h2>
        {/* {crearRolActivo && ( */}
        <Button
          title="Crear Perfil"
          className="bg-blue-500 hover:bg-blue-600 text-white"
          onClick={() => navigate("/rol/crear")}
        >
          <FontAwesomeIcon icon={faSquarePlus} className="mr-2" /> Crear Rol
        </Button>
        {/* )} */}
      </div>
      <div
        className={`shadow-md rounded-lg p-4 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <CustomTable
          {...{
            data: queryRol.data,
            columns: [
              {
                header: "ID",
                accessorKey: "id",
              },
              {
                header: "Nombre",
                accessorKey: "nombre",
              },
              {
                header: "Descripción",
                accessorKey: "descripcion",
              },
              {
                id: "estatus",
                header: "Estatus",
                accessorFn: (row) =>
                  row.estatus === 1 ? "ACTIVO" : "ELIMINADO",
                cell: (info) => info.getValue(),
              },
              {
                header: "Acciones",
                cell: (info) => (
                  <fieldset
                    className="btn-group"
                    role="group"
                    aria-label="Ejemplo"
                  >
                    {info.row.original.estatus === 0 && (
                      <Button
                        title="Reactivar rol"
                        className={classes.boton}
                        onClick={() =>
                          mutateRol.mutate({
                            id: info.row.original.id,
                            data: { estatus: 1 },
                            tipo: "patch",
                          })
                        }
                      >
                        <FontAwesomeIcon icon={faRotateLeft} />
                      </Button>
                    )}
                    {info.row.original.estatus === 1 && (
                      <Button
                        title="Eliminar rol"
                        className={classes.boton}
                        onClick={() =>
                          mutateRol.mutate({
                            id: info.row.original.id,
                            data: { estatus: 0 },
                            tipo: "delete",
                          })
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    )}
                    {/* {editarRolActivo && ( */}
                    <Button
                      title="Editar rol"
                      className={classes.boton}
                      onClick={() => handleEditClick(info.row.original)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    {/* )} */}
                  </fieldset>
                ),
              },
            ],
          }}
        />
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header
          closeButton
          className={`${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <Modal.Title className={`${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
            Editar Rol
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={`${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
          <Tabs defaultActiveKey="perfil" id="rol-edit-tabs">
            <Tab eventKey="perfil" title="Perfil">
              <Form>
                <Form.Group controlId="formRolNombre">
                  <Form.Label>Nombre del Rol</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingrese el nombre del rol"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                  />
                </Form.Group>
              </Form>
            </Tab>
            <Tab eventKey="permisos" title="Permisos">
              <CustomTable
                data={queryPermisos.data}
                columns={permisosColumns}
              />
            </Tab>
            <Tab eventKey="bloques" title="Bloques">
              <div>
                <CustomTable
                  data={queryBloques.data}
                  columns={bloquesColumns}
                />
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <div
          className={`${
            isDarkMode ? "bg-gray-800" : "bg-white"
          } flex justify-end space-x-4 p-4`}
        >
          <Button variant="secondary" onClick={handleCloseModal}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Guardar Cambios
          </Button>
        </div>
      </Modal>
    </div>
  );
};
